import React, {PropsWithChildren} from 'react';
import {chain, ListIcon, OntologyIcon} from '@genestack/ui';
import styles from './right-panel-wrapper.module.css';
import {
    VerticalBar,
    VerticalBarItem
} from '../../../../components/layout/vertical-bar/vertical-bar';
import {EditRightPanelTab} from '../../../../components/layout/page-layout/layout-typings';
import {
    CustomResizableLayout,
    ResizablePanel
} from '../../../../components/layout/resizable-layout';
import {OntologyTreeSidebar} from './ontology-tree-sidebar';
import {useFoldingItemsState} from '../../../../hooks/use-folding-items-state';
import {MetadataSidebar} from './metadata-sidebar';
import {Sidebar} from '../../../../components/layout/sidebar';
import {useDocumentSidebarState} from './hooks/use-document-sidebar-state';
import {useHasOntologyTree} from './hooks/use-has-ontology-tree';

const SIDEBAR_TABS: VerticalBarItem[] = [
    {
        tooltip: 'Doc metadata',
        value: EditRightPanelTab.METADATA,
        prepend: <ListIcon />
    }
];

const TREE_SIDEBAR_TAB: VerticalBarItem = {
    tooltip: 'Hierarchical tree',
    value: EditRightPanelTab.HIERARCHICAL_TREE,
    prepend: <OntologyIcon />
};

const NOTIONS_LIST_SIDEBAR_TAB: VerticalBarItem = {
    ...TREE_SIDEBAR_TAB,
    tooltip: 'Notions list'
};

interface Props {
    docInfo?: EditModeDoc | ViewModeDoc;
    rightSidebarProps: ReturnType<typeof useDocumentSidebarState>;
    hasOntologyTreeProps: ReturnType<typeof useHasOntologyTree>;
}

interface EditModeProps extends Props {
    docInfo: EditModeDoc;
    focusNode: (nodeId: string) => void;
    focusedTreeNode?: string;
}

interface ViewModeProps extends Props {
    docInfo?: ViewModeDoc;
    globalVersion: number;
}

function isExploreModeProps(props: EditModeProps | ViewModeProps): props is ViewModeProps {
    return 'globalVersion' in props;
}

export function RightPanelWrapper(props: PropsWithChildren<EditModeProps | ViewModeProps>) {
    const foldingItemsState = useFoldingItemsState();
    const isExploreView = isExploreModeProps(props);
    const {isSidebarOpen, selectedTab, closeSidebar, setSelectedTab, openSidebar} =
        props.rightSidebarProps;
    const {hasOntologyTree} = props.hasOntologyTreeProps;

    if (!props.docInfo) {
        return props.children;
    }

    const treeSidebarTab =
        props.docInfo.type === 'NOTIONS' ? NOTIONS_LIST_SIDEBAR_TAB : TREE_SIDEBAR_TAB;

    return (
        <div className={styles.rightPanelWrapper}>
            <CustomResizableLayout
                localStorageKey={`documents.${isExploreView ? 'explore' : 'edit'}.rightSidebar.layoutSize`}
                settings={{initialFirstPanelShare: 0.6, minPermittedHeight: 200}}
                alignment="horizontal"
                className={styles.selectedQueryBody}
                fixedPanelSize={isSidebarOpen ? undefined : {panelIndex: 1, size: 0}}
            >
                {(resizableLayoutProps) => (
                    <React.Fragment>
                        <ResizablePanel index={0} {...resizableLayoutProps}>
                            {props.children}
                        </ResizablePanel>

                        {isSidebarOpen && (
                            <ResizablePanel index={1} {...resizableLayoutProps}>
                                {(function () {
                                    if (!props.docInfo) {
                                        return <Sidebar />;
                                    }

                                    if (selectedTab === EditRightPanelTab.METADATA) {
                                        if (isExploreView) {
                                            return (
                                                <MetadataSidebar
                                                    onClose={closeSidebar}
                                                    document={props.docInfo}
                                                    globalVersion={props.globalVersion}
                                                />
                                            );
                                        }

                                        return (
                                            <MetadataSidebar
                                                onClose={closeSidebar}
                                                document={props.docInfo}
                                            />
                                        );
                                    }

                                    if (
                                        !isExploreView &&
                                        hasOntologyTree &&
                                        selectedTab === EditRightPanelTab.HIERARCHICAL_TREE
                                    ) {
                                        return (
                                            <OntologyTreeSidebar
                                                onClose={closeSidebar}
                                                docInfo={props.docInfo}
                                                foldingItemsProps={foldingItemsState}
                                                focusNode={props.focusNode!}
                                                focusedNode={props.focusedTreeNode}
                                            />
                                        );
                                    }
                                })()}
                            </ResizablePanel>
                        )}
                    </React.Fragment>
                )}
            </CustomResizableLayout>
            {/* use wrapper to prevent wide activity bar in imported ontologies */}
            <div className={styles.activityBarWrapper}>
                <VerticalBar
                    tabs={hasOntologyTree ? [...SIDEBAR_TABS, treeSidebarTab] : SIDEBAR_TABS}
                    value={isSidebarOpen ? selectedTab : undefined}
                    onValueChange={chain(setSelectedTab, openSidebar)}
                    placement="right"
                />
            </div>
        </div>
    );
}
